import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon } from "./icons";
import { API_ENDPOINTS } from "./constants";
import { generateEditUrl } from '../../core/utils/urlUtils';
import { formatLastEdited } from '../../core/utils/dateUtils';
import { apiFetch } from "../../core/utils/api";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";

const VersionManagement = ({
  website,
  username,
  domain,
  currentVersionSlug,
  setVersionManagementDropdownOpen,
  preferences
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const ITEMS_PER_PAGE = 4;

  useEffect(() => {
    const fetchVersions = async () => {
      if (!website?.container_id) {
        setError('No container found for this website');
        setLoading(false);
        return;
      }

      try {
        const response = await apiFetch(API_ENDPOINTS.CONTAINER_VERSIONS(website.container_id), {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch versions');
        }

        const data = await response.json();
        // Support both legacy array responses and the current { versions: [...] } shape.
        const normalizedVersions = Array.isArray(data) ? data : (data?.versions || []);
        setVersions(Array.isArray(normalizedVersions) ? normalizedVersions : []);
      } catch (err) {
        console.error('Error fetching versions:', err);
        setError('Failed to load versions');
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [website?.container_id]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Filter versions based on search term
  const filteredVersions = versions.filter(version => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (version.version_name || '').toLowerCase().includes(searchLower) ||
      (version.version_slug || '').toLowerCase().includes(searchLower)
    );
  });

  // Remove pagination logic
  // const totalPages = Math.ceil(filteredVersions.length / ITEMS_PER_PAGE);
  // const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  // const endIndex = startIndex + ITEMS_PER_PAGE;
  // const currentVersions = filteredVersions.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Scroll to current version when dropdown opens or search changes
  useEffect(() => {
    if (!listRef.current) return;
    const currentIndex = filteredVersions.findIndex(v => v.version_slug === currentVersionSlug);
    if (currentIndex === -1) return;
    // Each card is about 88px tall (including margin)
    const itemHeight = 88;
    listRef.current.scrollTop = currentIndex * itemHeight;
  }, [currentVersionSlug, filteredVersions]);

  const handleVersionClick = (version) => {
    if (version.version_slug === currentVersionSlug) {
      // Already on this version
      return;
    }

    const editUrl = generateEditUrl(username, domain, version.version_slug);
    navigate(editUrl);
    setVersionManagementDropdownOpen(false);
  };

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setSearchTerm('');
    }
  };

  const formatVersionDate = (version) => {
    const date = version.last_edited_at || version.updated_at;
    return formatLastEdited(date, preferences?.timezone);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${typography.h4} ${textColors.primary}`}>Website Versions</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className={`w-6 h-6 animate-spin border-2 border-gray-400 dark:border-gray-600 border-t-transparent rounded-full`}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${typography.h4} ${textColors.primary}`}>Website Versions</h3>
        </div>
        <div className={`${typography.bodySmall} text-red-600 dark:text-red-400 py-4`}>{error}</div>
        <div className="flex justify-end mt-4">
          <button
            onClick={() => setVersionManagementDropdownOpen(false)}
            className={`px-4 py-2 ${typography.button} ${textColors.secondary} ${textColors.hover}`}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const showPagination = filteredVersions.length > ITEMS_PER_PAGE;
  const showSearchIcon = versions.length > ITEMS_PER_PAGE;

  return (
    <div className="p-4">
      {/* Header with title and search icon */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`${typography.h4} ${textColors.primary}`}>Website Versions</h3>
        {filteredVersions.length > 4 && (
          <button
            onClick={handleSearchToggle}
            className={`p-1 ${textColors.secondary} hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 hover:dark:bg-blue-900/20 rounded-md transition-colors`}
            title="Search versions"
          >
            <SearchIcon />
          </button>
        )}
      </div>

      {/* Search input (collapsible) */}
      {isSearchOpen && (
        <div className="mb-4">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search versions..."
              className={`w-full px-3 py-2 pr-8 border ${borders.default} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 ${typography.bodySmall} ${surfaces.primary} ${textColors.primary} placeholder-gray-500 dark:placeholder-gray-400`}
            />
            <div className={`absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none ${textColors.muted}`}>
              <SearchIcon />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={`absolute inset-y-0 right-8 pr-2 flex items-center ${textColors.muted} ${textColors.hover}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchTerm && filteredVersions.length === 0 && (
            <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>
              No versions found for "{searchTerm}"
            </div>
          )}
        </div>
      )}
      
      {/* Versions list as a scrollable area */}
      <div ref={listRef} className="space-y-2 overflow-y-auto" style={{ maxHeight: "352px", minHeight: '352px' }}>
        {filteredVersions.map((version) => {
          const isCurrentVersion = version.version_slug === currentVersionSlug;
          const isPublished = version.is_published;
          
          return (
            <div
              key={version.id}
              onClick={() => handleVersionClick(version)}
              className={`p-3 rounded-lg border transition-all cursor-pointer ${
                isCurrentVersion
                  ? 'border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 hover:dark:bg-blue-900/30'
                  : `border-gray-200 dark:border-gray-700 ${surfaces.hover}`
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={`${typography.bodySmall} font-medium ${
                      isCurrentVersion ? 'text-blue-900 dark:text-blue-300' : textColors.primary
                    }`}>
                      {version.version_name || 'Untitled'}
                    </span>
                    {isCurrentVersion && (
                      <span
                        className="px-2 py-1 bg-blue-600 dark:bg-blue-600 rounded-full text-xs tracking-wide font-medium text-white"
                      >
                        Current
                      </span>
                    )}
                    {isPublished && (
                      <span className={`${typography.caption} px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full`}>
                        Published
                      </span>
                    )}
                  </div>
                  <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
                    Last edited: {formatVersionDate(version)}
                  </div>
                </div>
                
                {!isCurrentVersion && (
                  <div className={textColors.muted}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredVersions.length === 0 && !searchTerm && (
        <div className={`text-center py-8 ${typography.body} ${textColors.secondary}`}>
          <div className={`${textColors.muted} text-4xl mb-2`}>📄</div>
          <p className={typography.bodySmall}>No versions found</p>
        </div>
      )}

      <div className={`flex justify-end mt-6 pt-4 border-t ${borders.default}`}>
        <button
          onClick={() => setVersionManagementDropdownOpen(false)}
          className={`px-4 py-2 ${typography.button} ${textColors.secondary} ${textColors.hover}`}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default VersionManagement; 